import React, { useState } from "react";
import "@/App.css";
import { SimulationProvider, useSimulation } from "./context/SimulationContext";
import { GlobalHub } from "./components/GlobalHub";
import { LanguageLab } from "./components/LanguageLab";
import { PrepCardBuilder } from "./components/PrepCardBuilder";
import { Simulation } from "./components/Simulation";
import { ImpactDashboard } from "./components/ImpactDashboard";
import { Onboarding } from "./components/Onboarding";
import { ModuleMap } from "./components/ModuleMap";
import { Toaster } from "./components/ui/sonner";
import { Map, Home } from "lucide-react";

function AppContent() {
  const { state, resetSimulation, setView } = useSimulation();
  const { currentView, simulation } = state;
  const [mapOpen, setMapOpen] = useState(false);

  const [onboardingDone, setOnboardingDone] = useState(
    () => sessionStorage.getItem('stride_onboarding_complete') === 'true'
  );

  const handleOnboardingComplete = () => {
    sessionStorage.setItem('stride_onboarding_complete', 'true');
    setOnboardingDone(true);
  };

  const handleGoHome = () => {
    sessionStorage.removeItem('stride_onboarding_complete');
    resetSimulation();
    setView('hub');
    setOnboardingDone(false);
  };

  // Which view is currently active (for conditional button visibility)
  const isOnHub = onboardingDone && !simulation.active && currentView === 'hub';

  // Determine which view to render
  const renderView = () => {
    if (!onboardingDone) {
      return <Onboarding onComplete={handleOnboardingComplete} />;
    }
    if (simulation.active && currentView !== 'dashboard') {
      return <Simulation />;
    }
    switch (currentView) {
      case 'hub': return <GlobalHub onGoHome={handleGoHome} />;
      case 'theory': return <LanguageLab />;
      case 'prep': return <PrepCardBuilder />;
      case 'simulation': return <Simulation />;
      case 'dashboard': return <ImpactDashboard />;
      default: return <GlobalHub onGoHome={handleGoHome} />;
    }
  };

  return (
    <div className="App min-h-screen bg-[#F8FAFC]" data-testid="leader-cadence-app">
      {renderView()}

      {/* Home button — visible after onboarding, hidden on hub (nav has its own) */}
      {onboardingDone && !isOnHub && (
        <button
          onClick={handleGoHome}
          className="fixed top-4 right-4 sm:top-6 sm:right-6 z-40 w-9 h-9 flex items-center justify-center bg-[#002B45]/80 backdrop-blur-sm text-white/50 hover:text-white hover:bg-[#002B45] transition-all"
          data-testid="home-button"
          title="Restart course"
        >
          <Home className="w-4 h-4" strokeWidth={1.5} />
        </button>
      )}

      {/* Module Map Drawer */}
      <ModuleMap isOpen={mapOpen} onClose={() => setMapOpen(false)} />

      {/* Module Map Trigger */}
      {onboardingDone && (
        <button
          onClick={() => setMapOpen(true)}
          className="fixed bottom-8 left-8 z-30 w-12 h-12 bg-[#002B45] text-white flex items-center justify-center shadow-lg hover:bg-[#0055A5] transition-colors"
          data-testid="module-map-trigger"
          title="Module Map"
        >
          <Map className="w-5 h-5" strokeWidth={1.5} />
        </button>
      )}

      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <SimulationProvider>
      <AppContent />
    </SimulationProvider>
  );
}

export default App;
